export const LAST_LOGIN_HINT_STORAGE_KEY = 'aumhum:lastLoginHint'

const LOGIN_METHODS = {
  EMAIL: 'email',
  MOBILE: 'mobile',
  WHATSAPP: 'whatsapp',
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
}

const SOCIAL_METHODS = new Set([
  LOGIN_METHODS.GOOGLE,
  LOGIN_METHODS.FACEBOOK,
  LOGIN_METHODS.APPLE,
])

const PHONE_METHODS = new Set([LOGIN_METHODS.MOBILE, LOGIN_METHODS.WHATSAPP])

const METHOD_LABELS = {
  [LOGIN_METHODS.EMAIL]: 'Email',
  [LOGIN_METHODS.MOBILE]: 'Mobile number',
  [LOGIN_METHODS.WHATSAPP]: 'WhatsApp',
  [LOGIN_METHODS.GOOGLE]: 'Google',
  [LOGIN_METHODS.FACEBOOK]: 'Facebook',
  [LOGIN_METHODS.APPLE]: 'Apple',
}

function getStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

function normalizeValue(value) {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (typeof value !== 'string') {
    return ''
  }

  return value.trim()
}

function normalizePhoneValue(value) {
  const normalized = normalizeValue(value)
  if (!normalized) {
    return ''
  }

  const compact = normalized.replace(/[\s().-]/g, '')
  if (!compact) {
    return ''
  }

  return compact.startsWith('+') ? compact : `+${compact}`
}

export function isEmailLoginIdentifier(value) {
  const normalized = normalizeValue(value)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
}

export function normalizeLoginMethod(method, identifier) {
  const normalizedMethod = normalizeValue(method).toLowerCase()
  if (normalizedMethod === 'gmail') {
    return LOGIN_METHODS.GOOGLE
  }
  if (Object.values(LOGIN_METHODS).includes(normalizedMethod)) {
    return normalizedMethod
  }
  if (isEmailLoginIdentifier(identifier)) {
    return LOGIN_METHODS.EMAIL
  }
  if (normalizeValue(identifier)) {
    return LOGIN_METHODS.MOBILE
  }
  return ''
}

export function isPhoneLoginMethod(method) {
  return PHONE_METHODS.has(normalizeLoginMethod(method))
}

export function isSocialLoginMethod(method) {
  return SOCIAL_METHODS.has(normalizeLoginMethod(method))
}

export function getLoginMethodLabel(method) {
  return METHOD_LABELS[normalizeLoginMethod(method)] || 'Previous login'
}

function profileIdentityCandidates(profileData) {
  return [
    profileData?.userName,
    profileData?.username,
    profileData?.loginId,
    profileData?.primaryContact,
    profileData?.email,
    profileData?.emailId,
    profileData?.mobile,
    profileData?.phoneNumber,
    profileData?.communicationPref?.voiceCall?.uid,
    profileData?.info?.userName,
    profileData?.info?.username,
    profileData?.info?.email,
  ].map(normalizeValue).filter(Boolean)
}

function createHint(hintLike = {}) {
  const { method, identifier, displayName, updatedAt } = hintLike || {}
  const normalizedMethod = normalizeLoginMethod(method, identifier)
  if (!normalizedMethod) {
    return null
  }

  const normalizedIdentifier = PHONE_METHODS.has(normalizedMethod)
    ? normalizePhoneValue(identifier)
    : normalizeValue(identifier)

  if (!normalizedIdentifier && !SOCIAL_METHODS.has(normalizedMethod)) {
    return null
  }

  return {
    method: normalizedMethod,
    identifier: normalizedIdentifier,
    displayName: normalizeValue(displayName),
    updatedAt: normalizeValue(updatedAt) || new Date().toISOString(),
  }
}

export function createLastLoginHintFromProfile(profileData) {
  if (!profileData) {
    return null
  }

  const email = normalizeValue(profileData?.email || profileData?.emailId || profileData?.info?.email)
  if (email) {
    return createHint({
      method: LOGIN_METHODS.EMAIL,
      identifier: email,
      displayName: profileData?.name || profileData?.firstName || profileData?.userName,
    })
  }

  const mobile = normalizeValue(
    profileData?.mobile ||
      profileData?.phoneNumber ||
      profileData?.primaryContact ||
      profileData?.communicationPref?.voiceCall?.uid,
  )
  if (mobile) {
    return createHint({
      method: LOGIN_METHODS.MOBILE,
      identifier: mobile,
      displayName: profileData?.name || profileData?.firstName || profileData?.userName,
    })
  }

  const fallback = profileIdentityCandidates(profileData)[0]
  if (!fallback) {
    return null
  }

  return createHint({
    method: normalizeLoginMethod(null, fallback),
    identifier: fallback,
    displayName: profileData?.name || profileData?.firstName || profileData?.userName,
  })
}

export function getLastLoginHint() {
  const storage = getStorage()
  if (!storage) {
    return null
  }

  try {
    const parsed = JSON.parse(storage.getItem(LAST_LOGIN_HINT_STORAGE_KEY))
    return createHint(parsed)
  } catch {
    return null
  }
}

export function rememberLastLoginHint(hintLike) {
  const hint = createHint(hintLike)
  const storage = getStorage()
  if (!hint || !storage) {
    return null
  }

  storage.setItem(LAST_LOGIN_HINT_STORAGE_KEY, JSON.stringify(hint))
  return hint
}

function normalizeForComparison(value) {
  return normalizeValue(value).replace(/[\s().-]/g, '').replace(/^\+/, '').toLowerCase()
}

export function rememberLastLoginHintFromProfile(profileData) {
  const profileHint = createLastLoginHintFromProfile(profileData)
  if (!profileHint) {
    return null
  }

  const existingHint = getLastLoginHint()
  if (existingHint && SOCIAL_METHODS.has(existingHint.method) && !existingHint.identifier) {
    return rememberLastLoginHint({
      ...profileHint,
      method: existingHint.method,
    })
  }

  if (existingHint?.identifier) {
    const profileCandidates = profileIdentityCandidates(profileData).map(normalizeForComparison)
    const existingIdentifier = normalizeForComparison(existingHint.identifier)
    if (profileCandidates.includes(existingIdentifier)) {
      return rememberLastLoginHint({
        ...profileHint,
        method: existingHint.method,
        identifier: existingHint.identifier,
      })
    }
  }

  return rememberLastLoginHint(profileHint)
}

export function getLastLoginActionLabel(hintLike) {
  const hint = createHint(hintLike)
  if (!hint) {
    return 'Continue'
  }

  if (hint.method === LOGIN_METHODS.EMAIL) {
    return 'Use this email'
  }
  if (hint.method === LOGIN_METHODS.MOBILE) {
    return 'Use this mobile number'
  }
  if (hint.method === LOGIN_METHODS.WHATSAPP) {
    return 'Use this WhatsApp number'
  }
  return `Continue with ${getLoginMethodLabel(hint.method)}`
}

export function getLastLoginPreferredInputType(hintLike) {
  const hint = createHint(hintLike)
  if (!hint) {
    return null
  }

  if (hint.method === LOGIN_METHODS.EMAIL) {
    return LOGIN_METHODS.EMAIL
  }
  if (hint.method === LOGIN_METHODS.WHATSAPP) {
    return LOGIN_METHODS.WHATSAPP
  }
  if (hint.method === LOGIN_METHODS.MOBILE) {
    return LOGIN_METHODS.MOBILE
  }
  return null
}

export function getLastLoginHandoffUrl(hintLike) {
  const hint = createHint(hintLike)
  if (!hint || !PHONE_METHODS.has(hint.method)) {
    return '/'
  }

  const params = new URLSearchParams()
  params.set('lt', hint.method === LOGIN_METHODS.WHATSAPP ? 'w' : 't')
  if (hint.identifier) {
    params.set('loginUserName', hint.identifier)
  }
  return `/?${params.toString()}`
}
